import {Socket} from "socket.io-client";
import {MediasoupManagerHandle} from "../core/MediasoupManager";
import {RoomState} from "../core/room";

export interface TrackContext {
  socket: Socket;
  state: RoomState;
  mediasoupManager: MediasoupManagerHandle;
  updateMediaState(mediaState: any): void;
}

export function createTrackActions(ctx: TrackContext) {
  const {socket, state, mediasoupManager, updateMediaState} = ctx;

  async function produceTrack(
    track: MediaStreamTrack,
    appData?: {isScreenshare?: boolean}
  ): Promise<any> {
    const producer = await mediasoupManager.produce(track, appData);

    if (state.localParticipant) {
      state.localParticipant.producers.set(producer.id, producer);
      if (track.kind === "audio") {
        state.localParticipant.audioTrack = track;
        state.localParticipant.mediaState.audio = true;
      } else if (track.kind === "video") {
        if (appData?.isScreenshare) {
          (state.localParticipant as any).screenshareTrack = track;
          state.localParticipant.mediaState.sharescreen = true;
        } else {
          state.localParticipant.videoTrack = track;
          state.localParticipant.mediaState.video = true;
        }
      }
      updateMediaState(state.localParticipant.mediaState);
    }
    return producer;
  }

  async function recreateProducers(): Promise<void> {
    if (!state.localParticipant) return;

    state.localParticipant.producers.clear();
    const tracksToReproduce: Array<{track: MediaStreamTrack; appData?: any}> =
      [];

    if (
      state.localParticipant.audioTrack &&
      state.localParticipant.audioTrack.readyState === "live"
    ) {
      tracksToReproduce.push({track: state.localParticipant.audioTrack});
    }
    if (
      state.localParticipant.videoTrack &&
      state.localParticipant.videoTrack.readyState === "live"
    ) {
      tracksToReproduce.push({track: state.localParticipant.videoTrack});
    }
    const screenshareTrack = (state.localParticipant as any).screenshareTrack;
    if (screenshareTrack && screenshareTrack.readyState === "live") {
      tracksToReproduce.push({
        track: screenshareTrack,
        appData: {isScreenshare: true},
      });
    }

    for (const {track, appData} of tracksToReproduce) {
      try {
        await produceTrack(track, appData);
      } catch (error) {
        // Failed to recreate producer - track may need manual restart
      }
    }
  }

  async function stopScreenShare(): Promise<void> {
    if (!state.localParticipant || !state.room) return;

    const screenshareTrack = (state.localParticipant as any).screenshareTrack;
    if (screenshareTrack) {
      screenshareTrack.stop();
      (state.localParticipant as any).screenshareTrack = null;
    }

    for (const [producerId, producer] of state.localParticipant.producers) {
      if ((producer as any).appData?.isScreenshare) {
        producer.close();
        state.localParticipant.producers.delete(producerId);
        break;
      }
    }

    return new Promise((resolve) => {
      socket.emit(
        "stop-screenshare",
        {participantId: state.localParticipant!.participantId},
        () => {
          state.localParticipant!.mediaState.sharescreen = false;
          resolve();
        }
      );
    });
  }

  async function stopVideoProducer(): Promise<void> {
    if (!state.localParticipant || !state.room) return;

    const videoTrack = state.localParticipant.videoTrack;
    if (videoTrack) {
      videoTrack.stop();
      state.localParticipant.videoTrack = undefined;
    }

    for (const [producerId, producer] of state.localParticipant.producers) {
      if (
        producer.kind === "video" &&
        !(producer as any).appData?.isScreenshare
      ) {
        producer.close();
        state.localParticipant.producers.delete(producerId);
        break;
      }
    }

    return new Promise((resolve) => {
      socket.emit(
        "stop-video",
        {participantId: state.localParticipant!.participantId},
        () => {
          state.localParticipant!.mediaState.video = false;
          resolve();
        }
      );
    });
  }

  return {produceTrack, recreateProducers, stopScreenShare, stopVideoProducer};
}
